import { createContext, Dispatch, SetStateAction, useContext } from "react";
import { BatchApproveAllowancePreviewParams } from "./FormContext";
import { SmartAccountType } from "@wallet-manager/node-types/dist/src/postgres/const";

export type BatchApproveAllowancePreviewAddressData = {
  created_date: string;
  last_modified_date: string;
  merchant_id: number;
  chain_type: number;
  chain_id: number;
  asset_name: string;
  wallet_address: string;
  spender_address: string;
  wallet_type: number;
  wallet_balance: string;
  decimals: number;
  wallet_major_version: number;
};

export type BatchApproveAllowanceSubmitParams = {
  assetName: string;
  spenderAddress: string;
  chainType: string;
  chainId: string;
  orders: {
    walletAddress: string;
    walletType: number;
  }[];
  invokerAddress: string;
  smartAccountType?: SmartAccountType;
};

export type BatchApproveAllowancePreviewData = {
  previewParams: BatchApproveAllowancePreviewParams;
  addressData: BatchApproveAllowancePreviewAddressData[];
};

export type SendingAddressesMap = Map<
  string,
  {
    walletType: number;
    walletBalance: string;
    isSelected: boolean;
    walletMajorVersion: number;
  }
>;

export type BatchApproveAllowancePreviewContextType = {
  previewData: BatchApproveAllowancePreviewData | null;
  submitParams: BatchApproveAllowanceSubmitParams;
  walletMajorVersion: number;
  onSubmit: () => void;
  onReset: () => void;
  sendingAddressesMap: SendingAddressesMap;
  setSendingAddressesMap: Dispatch<SetStateAction<SendingAddressesMap>>;
  invokerAddressDictionary: Record<number, { invokerAddress: string }>;
  setInvokerAddressDictionary: Dispatch<
    SetStateAction<Record<number, { invokerAddress: string }>>
  >;
  invokerAddressSelectionDependency: {
    isSecondLayerToken: boolean;
    isSmartAccount: boolean;
  };
  getIsInvokerAddressSelectionShow: () => boolean;
  setWalletMajorVersion: Dispatch<SetStateAction<number>>;
  toDisplayAmountWithCurrency: (amount: string | number) => string;
  summaryDetails: {
    addressesSummary: {
      selectedCount: number;
      totalCount: number;
      totalBalance: string;
    };
    currency: string;
    chainName: string;
    spenderAddress: string;
  };
  translate: (key: string, params?: Record<string, any>) => string;
  tabsArr: { walletMajorVersion: number; walletCounts: number }[];
};

export const BatchApproveAllowancePreviewContext =
  createContext<BatchApproveAllowancePreviewContextType | null>(null);

export const useBatchApproveAllowancePreviewContext = () => {
  const context = useContext(BatchApproveAllowancePreviewContext);

  if (!context) {
    throw new Error(
      "useBatchApproveAllowancePreviewContext must be used within a BatchApproveAllowanceContextProvider",
    );
  }

  return context;
};
